import React, { useState, useRef, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  ArrowRight,
  CircleNotch,
  Funnel,
  ChatDots,
  PaperPlaneTilt,
  Flag,
  UserPlus,
  TrendUp,
  Bell,
  Lightning,
  PencilSimple,
  ShieldCheck,
  Eye,
  WarningCircle,
  CaretDown,
  X,
} from '@phosphor-icons/react';

/* ─── Helpers ─── */
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ─── Category config (minimal) ─── */
const CAT = {
  customer_enquiry:    { icon: ChatDots,      color: '#00E5C0' },
  lead_follow_up:      { icon: ArrowRight,    color: '#818CF8' },
  support_request:     { icon: Bell,          color: '#F59E0B' },
  complaint:           { icon: Flag,          color: '#FF6B6B' },
  spam:                { icon: Funnel,        color: '#7D8896' },
  newsletter:          { icon: Funnel,        color: '#7D8896' },
  internal_email:      { icon: Funnel,        color: '#7D8896' },
  supplier_vendor:     { icon: Funnel,        color: '#7D8896' },
  receipt_notification:{ icon: Funnel,        color: '#7D8896' },
  new_lead:            { icon: UserPlus,      color: '#00E5C0' },
  new_opportunity:     { icon: TrendUp,       color: '#00E5C0' },
  pipeline_progress:   { icon: TrendUp,       color: '#818CF8' },
  stalled_opportunity: { icon: Clock,         color: '#F59E0B' },
  follow_up_needed:    { icon: ArrowRight,    color: '#F59E0B' },
  data_incomplete:     { icon: WarningCircle, color: '#F59E0B' },
  won_customer:        { icon: CheckCircle,   color: '#00E5C0' },
  risk_flag:           { icon: Flag,          color: '#FF6B6B' },
  unknown:             { icon: Eye,           color: '#7D8896' },
};

/* ─── Action text per category (plain language) ─── */
const ACTION_TEXT = {
  customer_enquiry:    'Reply ready',
  lead_follow_up:      'Reply ready',
  support_request:     'Reply ready',
  complaint:           'Review before responding',
  new_lead:            'New lead details',
  new_opportunity:     'New opportunity',
  stalled_opportunity: 'Needs follow-up',
  follow_up_needed:    'Follow-up needed',
  data_incomplete:     'Incomplete details',
  won_customer:        'Deal won',
  risk_flag:           'Risk detected',
};

const SOURCE_LABELS = {
  gmail: 'Gmail', outlook: 'Outlook', hubspot: 'HubSpot', salesforce: 'Salesforce',
};


/* ═══════════════════════════════════════════════════
   ATTENTION HEADER
   ═══════════════════════════════════════════════════ */
export const AttentionHeader = ({ awaitingCount, autoCount, totalCount }) => {
  return (
    <div className="px-5 py-4 border-b border-[rgba(255,255,255,0.08)]" data-testid="attention-header">
      {awaitingCount > 0 ? (
        <>
          <p className="text-lg font-semibold text-[#F0F4F8]" data-testid="attention-count">
            {awaitingCount} need{awaitingCount === 1 ? 's' : ''} your attention
          </p>
          {autoCount > 0 && (
            <p className="text-xs text-[#7D8896] mt-0.5" data-testid="auto-handled-text">
              Your AI handled {autoCount} task{autoCount === 1 ? '' : 's'} automatically
            </p>
          )}
        </>
      ) : (
        <>
          {autoCount > 0 && (
            <p className="text-sm text-[#B0B8C4]" data-testid="auto-handled-text">
              Your AI handled {autoCount} task{autoCount === 1 ? '' : 's'} automatically. {totalCount > autoCount ? `${totalCount - autoCount} need your attention.` : ''}
            </p>
          )}
        </>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   SIGNAL ROW
   - customer name (primary)
   - action (secondary)
   - source / time (tertiary)
   - Complaints: no inline Send, must review
   - Non-complaints: inline Send
   ═══════════════════════════════════════════════════ */
export const SignalRow = ({ signal, isActive, onSelect, onApprove, onEdit, approving, onFadeOut }) => {
  const cat = signal.classification?.category || 'unknown';
  const style = CAT[cat] || CAT.unknown;
  const Icon = style.icon;
  const normalized = signal.normalized_payload || {};
  const execStatus = signal.execution?.status || 'logged';
  const needsAction = execStatus === 'awaiting_approval';
  const isEscalated = execStatus === 'escalated';
  const isComplaint = cat === 'complaint';
  const isAutoHandled = execStatus === 'auto_handled';
  const isActioned = execStatus === 'approved' || execStatus === 'dismissed' || execStatus === 'edited';

  const who = normalized.from_name || normalized.name || SOURCE_LABELS[signal.source_app] || signal.source_app || '';
  const source = SOURCE_LABELS[signal.source_app] || '';

  const action = needsAction
    ? (ACTION_TEXT[cat] || 'Review and approve')
    : isEscalated
      ? 'Needs your input'
      : isAutoHandled
        ? 'Handled automatically'
        : isActioned
          ? (execStatus === 'approved' ? 'Sent' : execStatus === 'edited' ? 'Edited' : 'Dismissed')
          : 'Handled automatically';

  // Fade-out animation state
  const [fading, setFading] = useState(false);

  const handleApprove = (e) => {
    e.stopPropagation();
    setFading(true);
    setTimeout(() => {
      onApprove(signal.signal_id, 'approve');
    }, 300);
  };

  return (
    <div
      className={`transition-all duration-300 ease-out ${fading ? 'opacity-0 max-h-0 overflow-hidden py-0' : 'opacity-100 max-h-48'}`}
      data-testid={`signal-${signal.signal_id}`}
    >
      <div
        className={`w-full px-5 py-3 border-b border-[rgba(255,255,255,0.04)] transition-colors cursor-pointer ${
          isActive ? 'bg-[rgba(0,229,192,0.04)]' : 'hover:bg-[rgba(255,255,255,0.02)]'
        }`}
        onClick={() => onSelect(signal)}
        data-testid={`signal-select-${signal.signal_id}`}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: `${style.color}12` }}
          >
            <Icon size={16} style={{ color: style.color }} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            {/* Primary: customer name */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-[#F0F4F8] truncate">{who}</p>
              <span className="text-[10px] text-[#7D8896] flex-shrink-0">{timeAgo(signal.received_at)}</span>
            </div>
            {/* Secondary: action */}
            <p className={`text-xs mt-0.5 truncate ${
              needsAction ? (isComplaint ? 'text-[#FF6B6B]' : 'text-[#F59E0B]')
              : isEscalated ? 'text-[#FF6B6B]'
              : isAutoHandled ? 'text-[#00E5C0]'
              : 'text-[#7D8896]'
            }`}>
              {action}
            </p>
            {/* Tertiary: source */}
            {source && <p className="text-[10px] text-[#7D8896] mt-0.5 truncate">{source}</p>}
          </div>
        </div>

        {/* Inline Send — for non-complaints that need action or are escalated */}
        {(needsAction || isEscalated) && !isComplaint && (
          <div className="flex items-center gap-2 mt-2 ml-11">
            <button
              onClick={handleApprove}
              disabled={approving === signal.signal_id}
              data-testid={`quick-approve-${signal.signal_id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#00E5C0] text-[#12161C] text-[11px] font-semibold rounded-md hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
            >
              {approving === signal.signal_id
                ? <CircleNotch size={11} className="animate-spin" />
                : <PaperPlaneTilt size={11} weight="bold" />}
              Send
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); (onEdit || onSelect)(signal); }}
              data-testid={`quick-edit-${signal.signal_id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] text-[#B0B8C4] border border-[rgba(255,255,255,0.08)] rounded-md hover:bg-[rgba(255,255,255,0.04)] transition-colors"
            >
              <PencilSimple size={11} />
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   SIGNAL DETAIL
   - Complaint: sensitive label, customer message, AI reply, edit helper
   - Non-complaint: clean chain with Send/Dismiss
   ═══════════════════════════════════════════════════ */
export const SignalDetail = ({ signal, onAction, acting, startInEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedReply, setEditedReply] = useState('');
  const [editedSubject, setEditedSubject] = useState('');

  // Sync edit buffer when selected signal changes, honoring startInEdit requests.
  useEffect(() => {
    if (!signal) return;
    setEditedReply(signal.draft_reply || '');
    setEditedSubject(signal.draft_reply_subject || '');
    setIsEditing(Boolean(startInEdit));
  }, [signal?.signal_id, startInEdit]);

  if (!signal) return null;

  const cat = signal.classification?.category || 'unknown';
  const style = CAT[cat] || CAT.unknown;
  const Icon = style.icon;
  const decision = signal.decision || {};
  const execution = signal.execution || {};
  const normalized = signal.normalized_payload || {};
  const isEmail = signal.source_type === 'email';
  const execStatus = execution.status || 'logged';
  const needsAction = execStatus === 'awaiting_approval';
  const isComplaint = cat === 'complaint';
  const draftReply = signal.draft_reply || '';
  const draftSubject = signal.draft_reply_subject || '';
  const hasSig = signal.draft_reply_has_signature || false;

  const who = normalized.from_name || normalized.name || '';

  return (
    <div className="p-6 space-y-5" data-testid="signal-detail">
      {/* ── Sensitive label for complaints ── */}
      {isComplaint && needsAction && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[rgba(255,107,107,0.06)] border border-[rgba(255,107,107,0.12)] rounded-lg" data-testid="sensitive-label">
          <Flag size={14} className="text-[#FF6B6B] flex-shrink-0" weight="fill" />
          <p className="text-xs text-[#FF6B6B] font-medium">Sensitive message — requires careful handling</p>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${style.color}15` }}>
          <Icon size={20} style={{ color: style.color }} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-semibold text-[#F0F4F8]">{who || 'Unknown sender'}</p>
          {isEmail && normalized.subject && (
            <p className="text-sm text-[#B0B8C4] mt-0.5 truncate">{normalized.subject}</p>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] text-[#7D8896]">{SOURCE_LABELS[signal.source_app] || signal.source_app}</span>
            <span className="text-[10px] text-[#7D8896]">{timeAgo(signal.received_at)}</span>
          </div>
        </div>
      </div>

      {/* ── Customer message ── */}
      {isEmail && normalized.body && (
        <div>
          <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-2 font-semibold">Customer message</p>
          <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
            <p className="text-sm text-[#B0B8C4] whitespace-pre-wrap leading-relaxed" data-testid="signal-email-body">
              {normalized.body}
            </p>
          </div>
        </div>
      )}

      {/* ── CRM record ── */}
      {!isEmail && normalized.name && (
        <div>
          <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-2 font-semibold">Record</p>
          <div className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-4 space-y-2">
            {normalized.name && <Field label="Name" value={normalized.name} />}
            {normalized.email && <Field label="Email" value={normalized.email} />}
            {normalized.company && <Field label="Company" value={normalized.company} />}
            {normalized.stage && <Field label="Stage" value={normalized.stage} />}
            {normalized.amount && <Field label="Value" value={`$${parseFloat(normalized.amount).toLocaleString()}`} />}
          </div>
        </div>
      )}

      {/* ── AI-prepared reply (email format) ── */}
      {draftReply && needsAction && (
        <div>
          <p className="text-[10px] text-[#7D8896] uppercase tracking-wider mb-2 font-semibold">AI-prepared reply</p>
          <div className="bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.08)] rounded-xl overflow-hidden" data-testid="draft-reply-email">
            {/* Subject line header */}
            {(draftSubject || isEditing) && (
              <div className="px-4 py-2.5 border-b border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.01)]">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#7D8896] shrink-0">Subject</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      data-testid="edit-signal-subject"
                      className="flex-1 bg-[rgba(255,255,255,0.03)] border border-[rgba(0,229,192,0.15)] rounded px-2 py-1 text-[#F0F4F8] focus:outline-none focus:ring-1 focus:ring-[rgba(0,229,192,0.3)]"
                    />
                  ) : (
                    <span className="text-[#F0F4F8] font-medium" data-testid="draft-reply-subject">{draftSubject}</span>
                  )}
                </div>
              </div>
            )}
            {/* Email body */}
            <div className="px-4 py-4">
              {isEditing ? (
                <textarea
                  value={editedReply}
                  onChange={(e) => setEditedReply(e.target.value)}
                  rows={8}
                  data-testid="edit-signal-reply-textarea"
                  autoFocus
                  className="w-full bg-[rgba(255,255,255,0.03)] border border-[rgba(0,229,192,0.15)] rounded-lg px-3 py-2.5 text-sm text-[#F0F4F8] focus:outline-none focus:ring-1 focus:ring-[rgba(0,229,192,0.3)] resize-y leading-relaxed"
                />
              ) : (
                <p className="text-sm text-[#F0F4F8] whitespace-pre-wrap leading-relaxed" data-testid="draft-reply-text">
                  {editedReply || draftReply}
                </p>
              )}
            </div>
            {/* Signature note */}
            {hasSig && (
              <div className="px-4 py-2 border-t border-[rgba(255,255,255,0.04)] bg-[rgba(255,255,255,0.01)]">
                <p className="text-[10px] text-[#5C6778] italic" data-testid="signature-note">Your email signature will be added automatically</p>
              </div>
            )}
          </div>
          <p className="text-[10px] text-[#7D8896] mt-1.5 italic">
            {isEditing ? 'Editing — click "Done editing" when finished' : 'You can edit this before sending'}
          </p>
        </div>
      )}

      {/* ── AI reasoning (inline context, no technical labels) ── */}
      {decision.reason && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-[rgba(255,255,255,0.02)] rounded-lg">
          <Lightning size={12} className="text-[#818CF8] mt-0.5 flex-shrink-0" weight="fill" />
          <p className="text-xs text-[#B0B8C4]">{decision.reason}</p>
        </div>
      )}

      {/* ── What we captured (collapsible) ── */}
      <CapturedData signal={signal} />

      {/* ── Actions ── */}
      {needsAction && (
        <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="detail-actions">
          {isComplaint ? (
            <>
              {draftReply && (
                <button
                  onClick={() => {
                    const hasEdits = (editedReply !== (signal.draft_reply || '')) || (editedSubject !== (signal.draft_reply_subject || ''));
                    const payload = hasEdits ? { edited_reply: editedReply, edited_subject: editedSubject } : undefined;
                    onAction(signal.signal_id, 'approve', payload);
                  }}
                  disabled={acting}
                  data-testid="detail-approve-btn"
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-semibold rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
                >
                  {acting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                  Send
                </button>
              )}
              <button
                onClick={() => setIsEditing((v) => !v)}
                data-testid="detail-edit-btn"
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-[#F0F4F8] border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              >
                <PencilSimple size={14} />
                {isEditing ? 'Done editing' : (draftReply ? 'Edit' : 'Write reply')}
              </button>
              <button
                onClick={() => onAction(signal.signal_id, 'dismiss')}
                disabled={acting}
                data-testid="detail-dismiss-btn"
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
              >
                Dismiss
              </button>
            </>
          ) : (
            <>
              {/* Non-complaint: Send + Edit + Dismiss */}
              <button
                onClick={() => {
                  const hasEdits = (editedReply !== (signal.draft_reply || '')) || (editedSubject !== (signal.draft_reply_subject || ''));
                  const payload = hasEdits ? { edited_reply: editedReply, edited_subject: editedSubject } : undefined;
                  onAction(signal.signal_id, 'approve', payload);
                }}
                disabled={acting}
                data-testid="detail-approve-btn"
                className="flex items-center gap-1.5 px-5 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-semibold rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50"
              >
                {acting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                Send
              </button>
              {draftReply && (
                <button
                  onClick={() => setIsEditing((v) => !v)}
                  data-testid="detail-edit-btn"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-[#F0F4F8] border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                >
                  <PencilSimple size={14} />
                  {isEditing ? 'Done editing' : 'Edit'}
                </button>
              )}
              <button
                onClick={() => onAction(signal.signal_id, 'dismiss')}
                disabled={acting}
                data-testid="detail-dismiss-btn"
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-[#7D8896] border border-[rgba(255,255,255,0.08)] rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors disabled:opacity-50"
              >
                Dismiss
              </button>
            </>
          )}
        </div>
      )}

      {/* ── Actioned state ── */}
      {(execStatus === 'approved' || execStatus === 'dismissed') && (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle size={16} className="text-[#00E5C0]" weight="fill" />
          <span className="text-sm text-[#B0B8C4]">{execStatus === 'approved' ? 'Sent' : 'Dismissed'}</span>
        </div>
      )}

      {/* ── Safety note ── */}
      <div className="flex items-center gap-2 pt-2">
        <ShieldCheck size={12} className="text-[#7D8896]" weight="fill" />
        <p className="text-[10px] text-[#7D8896]">Nothing is sent without your approval</p>
      </div>
    </div>
  );
};


/* ─── Tiny pieces ─── */
const Field = ({ label, value }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-[#7D8896] w-16 shrink-0">{label}</span>
    <span className="text-[#F0F4F8]">{value}</span>
  </div>
);


/* ═══════════════════════════════════════════════════
   CAPTURED DATA — Collapsible "What we captured"
   ═══════════════════════════════════════════════════ */
const SENTIMENT_LABELS = { positive: 'Positive', negative: 'Negative', neutral: 'Neutral', mixed: 'Mixed' };
const URGENCY_LABELS = { high: 'High', medium: 'Medium', low: 'Low', none: 'Low' };

const CapturedData = ({ signal }) => {
  const [open, setOpen] = useState(false);
  const classification = signal.classification || {};
  const normalized = signal.normalized_payload || {};
  const extracted = signal.extracted_fields || {};
  const isEmail = signal.source_type === 'email';

  // Build structured data rows from available signal data
  const rows = [];

  const name = normalized.from_name || normalized.name || extracted.name;
  if (name) rows.push({ label: 'Name', value: name });

  // Intent / issue
  const category = classification.category;
  const intentMap = {
    customer_enquiry: 'Customer enquiry',
    complaint: 'Complaint',
    support_request: 'Support request',
    lead_follow_up: 'Follow-up',
    new_lead: 'New lead',
    new_opportunity: 'New opportunity',
    pipeline_progress: 'Pipeline update',
    stalled_opportunity: 'Stalled deal',
    won_customer: 'Deal won',
    risk_flag: 'Risk flag',
  };
  if (intentMap[category]) rows.push({ label: 'Intent', value: intentMap[category] });

  if (classification.sentiment && classification.sentiment !== 'unknown')
    rows.push({ label: 'Sentiment', value: SENTIMENT_LABELS[classification.sentiment] || classification.sentiment });

  if (classification.urgency && classification.urgency !== 'none')
    rows.push({ label: 'Urgency', value: URGENCY_LABELS[classification.urgency] || classification.urgency });

  // Key details from normalized payload
  if (isEmail && normalized.subject) rows.push({ label: 'Subject', value: normalized.subject });
  if (normalized.email || normalized.from_email) rows.push({ label: 'Email', value: normalized.email || normalized.from_email });
  if (normalized.company) rows.push({ label: 'Company', value: normalized.company });
  if (normalized.stage) rows.push({ label: 'Stage', value: normalized.stage });
  if (normalized.amount) rows.push({ label: 'Value', value: `$${parseFloat(normalized.amount).toLocaleString()}` });

  // Extracted fields (from email extraction)
  if (extracted.service_type) rows.push({ label: 'Service', value: extracted.service_type });
  if (extracted.location) rows.push({ label: 'Location', value: extracted.location });
  if (extracted.phone) rows.push({ label: 'Phone', value: extracted.phone });

  if (rows.length === 0) return null;

  return (
    <div data-testid="captured-data">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
        data-testid="captured-data-toggle"
      >
        <CaretDown size={10} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
        What we captured
      </button>
      {open && (
        <div className="mt-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg p-3 space-y-1.5" data-testid="captured-data-content">
          {rows.map((r) => (
            <Field key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   SIMULATE PANEL
   ═══════════════════════════════════════════════════ */
export const SimulatePanel = ({ scenarios, onSimulate, simulating }) => {
  const [selected, setSelected] = useState('');

  return (
    <div className="px-5 py-3 border-b border-[rgba(255,255,255,0.08)]">
      <div className="flex gap-2">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="simulate-scenario-select"
          className="flex-1 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-1.5 text-xs text-[#F0F4F8] focus:outline-none focus:ring-1 focus:ring-[rgba(0,229,192,0.3)] truncate"
        >
          <option value="">Test with a sample event...</option>
          {scenarios.map((s) => (
            <option key={s.id} value={s.id}>{s.description}</option>
          ))}
        </select>
        <button
          onClick={() => { if (selected) onSimulate(selected); }}
          disabled={!selected || simulating}
          data-testid="simulate-run-btn"
          className="px-3 py-1.5 bg-[#00E5C0] text-[#12161C] text-xs font-semibold rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          {simulating ? <CircleNotch size={12} className="animate-spin" /> : 'Run'}
        </button>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════
   BATCH APPROVE PROMPT (contextual, not persistent)
   ═══════════════════════════════════════════════════ */
export const BatchApprovePrompt = ({ count, onApproveAll, acting }) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || count < 3) return null;

  return (
    <div className="mx-5 mt-2 mb-1 flex items-center gap-2 px-3 py-2 bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.08)] rounded-lg" data-testid="batch-approve-prompt">
      <p className="text-xs text-[#B0B8C4] flex-1">Approve all {count} similar replies?</p>
      <button
        onClick={onApproveAll}
        disabled={acting}
        data-testid="batch-approve-btn"
        className="text-[11px] font-semibold text-[#00E5C0] hover:underline disabled:opacity-50"
      >
        {acting ? 'Approving...' : 'Approve all'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-[#7D8896] hover:text-[#B0B8C4]"
        data-testid="batch-approve-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
};
